import React, { useState } from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { DatePicker, Input, Select } from "antd";
import dayjs from "dayjs";
import searchSrc from "../doc/plat/assets/search.svg";

const FILTER_KIND = {
  VID: "vid",
  SKL: "skl",
  ACT: "act"
};

const KIND_LABEL = {
  [FILTER_KIND.VID]: "Вид",
  [FILTER_KIND.SKL]: "Склад",
  [FILTER_KIND.ACT]: "Проведение"
};

const ACT_OPTIONS = [
  { value: 1, label: "Проведённые" },
  { value: 0, label: "Непроведённые" }
];

const MiniSelect = ({ autoFocus, defaultOpen, options, placeholder, value, onChange, width }) => {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  return (
    <Select
      autoFocus={autoFocus}
      open={open}
      onOpenChange={setOpen}
      showSearch
      optionFilterProp="label"
      placeholder={placeholder}
      value={value}
      options={options}
      popupMatchSelectWidth={false}
      variant="borderless"
      suffixIcon={null}
      style={{ width }}
      onChange={(next) => {
        setOpen(false);
        onChange?.(next);
      }}
    />
  );
};

const WarehouseSearchFilter = ({ value, documentTypes, warehouses, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [draftKind, setDraftKind] = useState(null);
  const [editKey, setEditKey] = useState(null);

  const patch = (next) => onChange?.({ ...value, ...next });
  const vidOptions = documentTypes || [];
  const sklOptions = warehouses || [];
  const hasVid = value?.vid != null;
  const hasSkl = (value?.idSkl ?? 0) > 0;
  const hasAct = value?.isAct === true || value?.isAct === false;
  const vidLabel = vidOptions.find((o) => o.value === value?.vid)?.label;
  const sklLabel = sklOptions.find((o) => o.value === value?.idSkl)?.label;
  const actLabel = hasAct ? (value.isAct ? "Проведённые" : "Непроведённые") : "";

  const chips = [];
  if (hasVid) {
    chips.push({
      key: FILTER_KIND.VID,
      kindLabel: KIND_LABEL[FILTER_KIND.VID],
      valueLabel: vidLabel || String(value.vid)
    });
  }
  if (hasSkl) {
    chips.push({
      key: FILTER_KIND.SKL,
      kindLabel: KIND_LABEL[FILTER_KIND.SKL],
      valueLabel: sklLabel || String(value.idSkl)
    });
  }
  if (hasAct) {
    chips.push({
      key: FILTER_KIND.ACT,
      kindLabel: KIND_LABEL[FILTER_KIND.ACT],
      valueLabel: actLabel
    });
  }

  const availableKinds = [
    vidOptions.length && !hasVid ? { value: FILTER_KIND.VID, label: KIND_LABEL[FILTER_KIND.VID] } : null,
    sklOptions.length && !hasSkl ? { value: FILTER_KIND.SKL, label: KIND_LABEL[FILTER_KIND.SKL] } : null,
    !hasAct ? { value: FILTER_KIND.ACT, label: KIND_LABEL[FILTER_KIND.ACT] } : null
  ].filter(Boolean);
  const canAdd = availableKinds.length > 0;
  const activeKind = editKey || draftKind;

  const cancelAdd = () => {
    setAdding(false);
    setDraftKind(null);
    setEditKey(null);
  };

  const applyKind = (kind, next) => {
    if (kind === FILTER_KIND.VID) patch({ vid: next });
    else if (kind === FILTER_KIND.SKL) patch({ idSkl: next || undefined });
    else if (kind === FILTER_KIND.ACT) patch({ isAct: next === 1 });
  };

  const clearChip = (kind) => {
    if (kind === FILTER_KIND.VID) patch({ vid: undefined });
    else if (kind === FILTER_KIND.SKL) patch({ idSkl: undefined });
    else if (kind === FILTER_KIND.ACT) patch({ isAct: undefined });
    if (editKey === kind) setEditKey(null);
  };

  const startAdd = () => {
    setEditKey(null);
    setDraftKind(availableKinds.length === 1 ? availableKinds[0].value : null);
    setAdding(true);
  };

  const pickDraftValue = (next) => {
    if (editKey) {
      applyKind(editKey, next);
      setEditKey(null);
      return;
    }
    if (draftKind == null) return;
    applyKind(draftKind, next);
    cancelAdd();
  };

  const valueOptions =
    activeKind === FILTER_KIND.VID
      ? vidOptions
      : activeKind === FILTER_KIND.SKL
        ? sklOptions
        : ACT_OPTIONS;
  const draftValue =
    editKey === FILTER_KIND.VID && hasVid
      ? value.vid
      : editKey === FILTER_KIND.SKL && hasSkl
        ? value.idSkl
        : editKey === FILTER_KIND.ACT && hasAct
          ? value.isAct
            ? 1
            : 0
          : undefined;

  return (
    <div className="warehouse-documents__search" role="group" aria-label="Поиск и фильтр">
      <span className="warehouse-documents__search-icon">
        <img src={searchSrc} alt="" />
      </span>
      <Input
        variant="borderless"
        allowClear
        className="warehouse-documents__search-input"
        value={value?.q || ""}
        placeholder="Номер, комментарий..."
        onChange={(e) => patch({ q: e.target.value })}
      />
      {chips.map((chip) =>
        editKey === chip.key ? (
          <span key={chip.key} className="warehouse-documents__search-draft">
            <span className="warehouse-documents__search-chip-kind">{chip.kindLabel}</span>
            <MiniSelect
              autoFocus
              defaultOpen
              options={valueOptions}
              placeholder="Значение"
              value={draftValue}
              width={160}
              onChange={pickDraftValue}
            />
            <button
              type="button"
              className="warehouse-documents__search-chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKey(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.key} className="warehouse-documents__search-chip">
            <button
              type="button"
              className="warehouse-documents__search-chip-btn"
              title="Изменить значение"
              onClick={() => {
                setAdding(false);
                setDraftKind(null);
                setEditKey(chip.key);
              }}
            >
              <span className="warehouse-documents__search-chip-kind">{chip.kindLabel}</span>
              <span className="warehouse-documents__search-chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="warehouse-documents__search-chip-clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={() => clearChip(chip.key)}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="warehouse-documents__search-draft">
          {draftKind == null ? (
            <MiniSelect
              autoFocus
              defaultOpen
              options={availableKinds}
              placeholder="Фильтр"
              value={draftKind}
              width={150}
              onChange={setDraftKind}
            />
          ) : (
            <>
              <span className="warehouse-documents__search-chip-kind">
                {availableKinds.find((k) => k.value === draftKind)?.label || KIND_LABEL[draftKind]}
              </span>
              <MiniSelect
                autoFocus
                defaultOpen
                options={valueOptions}
                placeholder="Значение"
                width={160}
                onChange={pickDraftValue}
              />
            </>
          )}
          <button
            type="button"
            className="warehouse-documents__search-chip-clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding && !editKey ? (
        <button
          type="button"
          className="warehouse-documents__search-add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

const WarehouseDocumentsFilters = ({ value, documentTypes, warehouses, onChange, children }) => (
  <div className="warehouse-documents__filters">
    <div className="warehouse-documents__field">
      <DatePicker
        allowClear={false}
        format="DD.MM.YYYY"
        value={value?.dateBeg ? dayjs(value.dateBeg) : null}
        suffixIcon={null}
        title="Дата с"
        aria-label="Дата с"
        onChange={(d) => d && onChange?.({ ...value, dateBeg: d.format("YYYY-MM-DD") })}
      />
    </div>
    <div className="warehouse-documents__field">
      <DatePicker
        allowClear={false}
        format="DD.MM.YYYY"
        value={value?.dateEnd ? dayjs(value.dateEnd) : null}
        suffixIcon={null}
        title="Дата по"
        aria-label="Дата по"
        onChange={(d) => d && onChange?.({ ...value, dateEnd: d.format("YYYY-MM-DD") })}
      />
    </div>
    <WarehouseSearchFilter
      value={value}
      documentTypes={documentTypes}
      warehouses={warehouses}
      onChange={onChange}
    />
    <div className="warehouse-documents__extra-actions">{children}</div>
  </div>
);

export default WarehouseDocumentsFilters;
